import type { SupabaseClient } from '@supabase/supabase-js';

/**
 * Membership plans.
 *
 * Everything the pricing page shows lives in the database — name, tagline,
 * prices, reveal allowance, feature list, button label, badge, and whether the
 * plan appears at all — so the client can change strategy without a deploy.
 */

export interface PlanFeature {
  label: string;
  included: boolean;
  note?: string;
}

export interface MembershipPlan {
  id: string;
  name: string;
  blurb: string | null;
  tagline: string | null;
  cta_label: string | null;
  badge: string | null;
  monthly_price_pence: number;
  annual_price_pence: number | null;
  /** null = unlimited */
  reveals_per_year: number | null;
  /** null = unlimited */
  listing_limit: number | null;
  /** How many days a listing stays live. null = never expires. */
  listing_days: number | null;
  /** Free boosts a month. null = unlimited, 0 = none. */
  boosts_per_month: number | null;
  /** null = unlimited */
  enquiry_limit: number | null;
  features: PlanFeature[];
  stripe_price_monthly: string | null;
  stripe_price_annual: string | null;
  sort_order: number;
  is_active: boolean;
  is_visible: boolean;
}

export interface VatSettings {
  pricesExVat: boolean;
  chargeVat: boolean;
  vatRatePercent: number;
}

export const DEFAULT_VAT: VatSettings = {
  pricesExVat: true,
  chargeVat: false,
  vatRatePercent: 20,
};

const COLUMNS =
  'id, name, blurb, tagline, cta_label, badge, monthly_price_pence, annual_price_pence, ' +
  'reveals_per_year, listing_limit, listing_days, boosts_per_month, enquiry_limit, ' +
  'features, stripe_price_monthly, stripe_price_annual, ' +
  'sort_order, is_active, is_visible';

/** Plans for the public pricing page. RLS hides invisible ones from non-admins. */
export async function fetchPlans(supabase: SupabaseClient): Promise<MembershipPlan[]> {
  const { data, error } = await supabase
    .from('membership_plans')
    .select(COLUMNS)
    .eq('is_active', true)
    .order('sort_order');
  if (error) throw error;
  return normalise(data);
}

/** Every plan, visible or not — admin editor. */
export async function fetchAllPlans(supabase: SupabaseClient): Promise<MembershipPlan[]> {
  const { data, error } = await supabase
    .from('membership_plans')
    .select(COLUMNS)
    .order('sort_order');
  if (error) throw error;
  return normalise(data);
}

function normalise(rows: unknown): MembershipPlan[] {
  return ((rows ?? []) as MembershipPlan[]).map((p) => ({
    ...p,
    features: Array.isArray(p.features) ? p.features : [],
  }));
}

export async function savePlan(
  supabase: SupabaseClient,
  id: string,
  patch: Partial<MembershipPlan>
): Promise<void> {
  const { error } = await supabase.from('membership_plans').update(patch).eq('id', id);
  if (error) throw error;
}

export async function fetchVatSettings(supabase: SupabaseClient): Promise<VatSettings> {
  const { data, error } = await supabase
    .from('site_settings')
    .select('prices_ex_vat, charge_vat, vat_rate_percent')
    .eq('id', 1)
    .maybeSingle();
  if (error || !data) return DEFAULT_VAT;
  return {
    pricesExVat: Boolean(data.prices_ex_vat),
    chargeVat: Boolean(data.charge_vat),
    vatRatePercent: Number(data.vat_rate_percent) || 20,
  };
}

export async function saveVatSettings(
  supabase: SupabaseClient,
  patch: Partial<{ pricesExVat: boolean; chargeVat: boolean; vatRatePercent: number }>
): Promise<void> {
  const row: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (patch.pricesExVat !== undefined) row.prices_ex_vat = patch.pricesExVat;
  if (patch.chargeVat !== undefined) row.charge_vat = patch.chargeVat;
  if (patch.vatRatePercent !== undefined) row.vat_rate_percent = patch.vatRatePercent;
  const { error } = await supabase.from('site_settings').update(row).eq('id', 1);
  if (error) throw error;
}

export function pounds(pence: number | null | undefined): number {
  return (pence ?? 0) / 100;
}

/** "£9.99" / "Free". Prices are held and shown ex VAT. */
export function priceLabel(pence: number | null | undefined): string {
  const value = pounds(pence);
  if (value === 0) return 'Free';
  return `£${value.toFixed(2)}`;
}

/** The suffix that keeps an ex-VAT price honest, e.g. "+ VAT". */
export function vatSuffix(vat: VatSettings, pence: number | null | undefined): string {
  if (!vat.pricesExVat || pounds(pence) === 0) return '';
  return vat.chargeVat ? `+ VAT` : `ex VAT`;
}

export function allowanceLabelForPlan(plan: MembershipPlan): string {
  if (plan.reveals_per_year === null) return 'Unlimited contact reveals';
  return `${plan.reveals_per_year} contact reveal${plan.reveals_per_year === 1 ? '' : 's'} a year`;
}
